import { FC, useState } from 'react';
import styles from './ReferralEarningsChart.module.css';

export interface ReferralEarningsChartProps {
    /** Number of bars to display */
    barCount?: number;
    /** Rate per creator per month (default: 6) */
    ratePerCreator?: number;
    /** Initial creator count */
    initialCreatorCount?: number;
    /** Min creators for slider */
    minCreators?: number;
    /** Max creators for slider */
    maxCreators?: number;
    /** Show title */
    showTitle?: boolean;
    /** Custom title */
    title?: string;
    /** Custom className */
    className?: string;
}

export const ReferralEarningsChart: FC<ReferralEarningsChartProps> = ({
    barCount = 10,
    ratePerCreator = 6,
    initialCreatorCount = 2,
    minCreators = 0,
    maxCreators = 10,
    showTitle = true,
    title = 'Potential Earnings',
    className = '',
}) => {
    const [creatorsCount, setCreatorsCount] = useState(initialCreatorCount);

    // Calculate earnings
    const monthlyEarnings = creatorsCount * ratePerCreator;
    const yearlyEarnings = monthlyEarnings * 12;

    const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCreatorsCount(parseInt(event.target.value));
    };

    // Calculate percentage for slider
    const percentage = ((creatorsCount - minCreators) / (maxCreators - minCreators)) * 100;

    return (
        <div className={`w-full pt-5 ${className}`}>
            {showTitle && <h2 className='text-dark text-heading-h2 mb-5'>{title}</h2>}

            <div className='flex w-full flex-col gap-4 pt-8'>
                {/* Bar Chart */}
                <div className={styles.chart}>
                    {Array.from({ length: barCount }, (_, index) => {
                        const barId = index + 1;

                        return (
                            <div key={barId} className={styles.barContainer}>
                                {/* First bar label */}
                                {barId === 1 && (
                                    <div className={`${styles.barLabel} ${styles.barLabelLeft}`}>
                                        <div className='text-brand-main text-label-sm-bold'>${monthlyEarnings}/mo</div>
                                        <div className='text-gray text-label-xs-regular mt-0.5'>after 1 month</div>
                                    </div>
                                )}
                                {/* Last bar label */}
                                {barId === barCount && (
                                    <div className={`${styles.barLabel} ${styles.barLabelRight}`}>
                                        <div className='text-brand-main text-label-sm-bold'>${yearlyEarnings}/mo</div>
                                        <div className='text-gray text-label-xs-regular mt-0.5'>after 12 months</div>
                                    </div>
                                )}
                                <div className={`${styles.bar} ${styles[`bar${barId}`]}`} />
                            </div>
                        );
                    })}
                </div>

                {/* Slider */}
                <div className='flex w-full flex-col items-center gap-2'>
                    <div className={styles.sliderTrack} data-percentage={Math.round(percentage / 10) * 10}>
                        <div className={styles.sliderActive} />
                        <input
                            type='range'
                            min={minCreators}
                            max={maxCreators}
                            value={creatorsCount}
                            onChange={handleSliderChange}
                            className={styles.slider}
                            aria-label='Number of creators'
                        />
                    </div>
                    <div className='text-brand-main text-label-sm-bold text-center'>{creatorsCount} creators/mo</div>
                </div>
            </div>
        </div>
    );
};
